// =============================================================================
// Toast — a single transient, auto-dismissing confirmation.
//
// The parent owns visibility (`show`) and clears it on its own timer, so the
// toast stays presentational. When `show` flips false the toast plays a brief
// fade-out before unmounting (rather than vanishing instantly), so the dismiss
// reads as a gentle settle. Used for the drawer's "Changes staged" confirmation
// (the drawer closes, so a momentary toast tells the user the edits landed in the
// table's pending bar). Not a general notification system.
//
// Re-pulsing on a repeated event (e.g. staging again while the toast is still up)
// is the parent's job: pass a changing `key` so React remounts this and the entry
// animation replays. This component only reacts to `show` transitions.
// =============================================================================
import { useEffect, useRef, useState } from 'react'

// Keep in step with the fade-out animation duration in index.css (.animate-toast-out).
const EXIT_MS = 260

// `position` controls placement:
//   'viewport-br'  — fixed to the browser bottom-right (default; general confirmations).
//   'container-tr' — absolute top-right of the nearest positioned ancestor, so it sits
//                    over that panel's content rather than the whole viewport.
//                    The parent must be `relative` for this to anchor correctly.
export function Toast({
  show,
  message,
  position = 'viewport-br',
}: {
  show: boolean
  message: string
  position?: 'viewport-br' | 'container-tr'
}) {
  // `render` keeps the node mounted through the exit animation after `show` → false.
  const [render, setRender] = useState(show)
  const [leaving, setLeaving] = useState(false)
  const mounted = useRef(show) // tracks `render` for the effect without re-triggering it
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Keyed on `show` alone (a ref, not `render`, backs the exit path) so the effect runs
  // once per visibility transition rather than twice. The exit branch is skipped unless
  // the toast is actually mounted, so a false→false initial mount arms no timer.
  useEffect(() => {
    if (show) {
      if (exitTimer.current) { clearTimeout(exitTimer.current); exitTimer.current = null }
      mounted.current = true
      setLeaving(false)
      setRender(true)
    } else if (mounted.current) {
      setLeaving(true)
      exitTimer.current = setTimeout(() => { mounted.current = false; setRender(false) }, EXIT_MS)
    }
    return () => { if (exitTimer.current) clearTimeout(exitTimer.current) }
  }, [show])

  if (!render) return null
  const wrapper =
    position === 'container-tr'
      ? 'absolute -top-1 right-0 justify-end'
      : 'fixed bottom-6 right-6 justify-end px-4'
  return (
    <div className={`pointer-events-none z-[80] flex ${wrapper}`}>
      <div className={`${leaving ? 'animate-toast-out' : 'animate-toast-in'} rounded-lg border border-brand-200 bg-slate-900 px-4 py-2 text-[12px] font-medium text-white shadow-lift`}>
        {message}
      </div>
    </div>
  )
}
